import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, ChapterTag, MaskedLine } from "@/components/Reveal";

const principles = [
  { num: "01", text: "Find the right technology." },
  { num: "02", text: "Connect the pieces." },
  { num: "03", text: "Solve the problem." },
  { num: "04", text: "Deliver results." },
];

const closing = [
  "He understands the hardware.",
  "He understands the software.",
  "He understands AI.",
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-20">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-20 md:pb-28">
        <ChapterTag num="08" label="About" />
        <MaskedLine className="mt-12 font-unbounded font-extrabold uppercase tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl">
          Built around
        </MaskedLine>
        <MaskedLine delay={0.12} className="font-unbounded font-extrabold uppercase tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl text-outline">
          Curiosity.
        </MaskedLine>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.3em] text-cyan sticky top-32">
                // WHAT'S NEXT — AND WHAT IT CAN SOLVE
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-8 space-y-7 max-w-3xl">
            <Reveal delay={0.05}>
              <p className="text-base md:text-lg text-neutral-300 leading-relaxed">
                My career in technology has been driven by curiosity about what's next — and how new technologies can solve real problems.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed">
                A key part of that experience is an extensive combination of software and hardware. I've worked with technologies ranging from physical tracking devices, wireless communications, sensors, smart glasses, and connected hardware to mobile applications, cloud platforms, dashboards, automation, computer vision, and artificial intelligence.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed">
                I was developing applications for wearable computers when Google Glass first arrived — building dozens of applications for the platform.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed">
                Today, I combine that broad technical background with the newest generation of AI technologies to help businesses understand what's possible — and turn ideas into working solutions.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-objective" className="border-t border-white/10 bg-[#070707] bg-blueprint">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
          <MaskedLine className="font-unbounded font-extrabold uppercase tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl">
            The technology changes.
          </MaskedLine>
          <MaskedLine delay={0.14} className="font-unbounded font-extrabold uppercase tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl text-cyan">
            The objective doesn't.
          </MaskedLine>
          <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {principles.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08}>
                <div data-testid={`principle-${p.num}`} className="bg-[#0A0A0A] p-8 h-full hover:bg-[#0d1114] transition-colors duration-500">
                  <span className="font-mono text-xs text-cyan">{p.num}</span>
                  <p className="mt-5 font-unbounded font-normal uppercase tracking-tight text-base md:text-lg text-white leading-snug">
                    {p.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-closing" className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.35em] text-neutral-500 mb-14">// THE SHORT VERSION</p>
          </Reveal>
          <div className="space-y-2">
            {closing.map((line, i) => (
              <MaskedLine
                key={line}
                delay={i * 0.12}
                className="font-unbounded font-semibold uppercase tracking-tight leading-[1.15] text-2xl sm:text-4xl lg:text-5xl text-neutral-300"
              >
                {line}
              </MaskedLine>
            ))}
            <MaskedLine
              delay={0.4}
              className="font-unbounded font-extrabold uppercase tracking-tight leading-[1.15] text-2xl sm:text-4xl lg:text-5xl text-cyan pt-4"
            >
              And he knows how to make them work together.
            </MaskedLine>
          </div>
          <Reveal delay={0.5}>
            <Link
              to="/contact"
              data-testid="about-cta-talk"
              className="group mt-16 inline-flex items-center gap-3 bg-cyan text-black px-8 py-4 font-mono text-xs tracking-[0.25em] font-medium hover:bg-cyan-hover transition-colors duration-300"
            >
              START THE CONVERSATION
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
